import React, { useMemo, useState } from 'react';
import ReactPlayer from 'react-player';

import { TextCollectionAttachment } from '@scale/shared/types/params/textcollection';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';

interface AudioAttachmentViewProps {
  attachment: TextCollectionAttachment;
  markAttachmentAsCompleted: (isComplete: boolean) => void;
  completed: boolean;
  isInline?: boolean;
  hidden?: boolean;
  preventSkip?: boolean;
  startSelfDestructSequence?: (secs: number) => void;
  responseWindow?: number;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
}

export default function AudioAttachmentView(props: AudioAttachmentViewProps) {
  const {
    attachment,
    markAttachmentAsCompleted,
    completed,
    isInline,
    preventSkip,
    startSelfDestructSequence,
    responseWindow,
    onFocus,
    onBlur,
    disabled,
  } = props;
  const [numPlays, setNumPlays] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState(0);
  const isBlocked = useMemo(
    () => attachment.blocking && attachment.autoplay && !completed,
    [attachment, completed],
  );
  const reachedMaxPlays = useMemo(
    () => attachment.max_num_plays && numPlays >= attachment.max_num_plays,
    [attachment, numPlays],
  );
  const showControls = !isBlocked && !reachedMaxPlays && !disabled;
  let player: $FIXME = null;
  const ref = (p: $FIXME) => (player = p);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  return (
    <>
      <ReactPlayer
        ref={ref}
        width={isInline ? '100%' : 600}
        height={!showControls ? 0 : isInline ? 50 : isSafari ? 84 : 54}
        url={attachment.content}
        controls={showControls}
        playsinline={isInline}
        config={{
          file: {
            attributes: {
              controlsList: 'nodownload',
            },
          },
        }}
        playing={!!attachment.autoplay && !props.hidden && !completed}
        onPlay={onFocus}
        onPause={onBlur}
        onEnded={() => {
          markAttachmentAsCompleted(true);
          onBlur && onBlur();
          setNumPlays(numPlays + 1);
          if (responseWindow && startSelfDestructSequence) {
            startSelfDestructSequence(responseWindow);
          }
        }}
        onSeek={() => {
          if (preventSkip) {
            const delta = player.getCurrentTime() - currentTime;
            if (delta > 0.01) {
              player.seekTo(currentTime);
            }
          }
        }}
        onProgress={({ playedSeconds }) => {
          if (preventSkip) {
            const delta = player.getCurrentTime() - currentTime;
            if (delta > 1.5) {
              player.seekTo(currentTime);
            } else {
              setCurrentTime(playedSeconds);
            }
          } else {
            setCurrentTime(playedSeconds);
          }
        }}
      />
      {isBlocked && <p>Playing audio...</p>}
      {attachment.max_num_plays && (
        <p>You have {attachment.max_num_plays - numPlays} plays remaining</p>
      )}
      {attachment.caption && <SanitizedMarkdown text={attachment.caption} />}
    </>
  );
}
